import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ReconciliationDocumentFileFormat,
  ReconciliationDocumentStatus,
  useDownloadReconciliationDocumentMutation,
  useFetchReconciliationDocumentStatusLazyQuery
} from '../../../../generated'
import { Alert } from '../../common/Alert'

/** How often the UI asks the API whether the background job has finished. */
const POLL_INTERVAL_MS = 2500
/**
 * Five minutes. Generation itself is quick, but the job may sit behind others on the queue, and a
 * poll that gives up early strands the user: the file still lands, they just aren't watching when
 * it does. On timeout the message tells them to click again, which is then an instant download.
 */
const POLL_TIMEOUT_MS = 300000

export interface PendingDocument {
  reconciliationId: number
  fileFormat: ReconciliationDocumentFileFormat
}

/**
 * Drives the "Download as PDF / CSV" actions on a finished reconciliation.
 *
 * The file is built by a background job on the API, so the first request for a given format returns
 * no URL — it queues the work. This hook then polls the document status and, once the job reports
 * COMPLETED, asks again and saves the file. Every later request for the same format is answered
 * straight from the cached S3 object, so it downloads immediately.
 */
export const useReconciliationDocument = () => {
  const [pending, setPending] = useState<PendingDocument | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const deadlineRef = useRef<number>(0)
  // Read inside the poll callback, which closes over its first render otherwise.
  const pendingRef = useRef<PendingDocument | null>(null)
  pendingRef.current = pending

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  const saveFile = useCallback(async (preSignedUrl: string) => {
    try {
      const response = await fetch(preSignedUrl)

      if (!response.ok) {
        Alert.error('Could not download the reconciliation document')
        return
      }

      const blob = await response.blob()
      // The S3 key carries the generated filename; everything after `?` is the signature.
      const pathParts = preSignedUrl.split('?')[0].split('/')
      const fileName = decodeURIComponent(pathParts[pathParts.length - 1]).replace(/^\d+_/, '')

      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch {
      Alert.error('Could not download the reconciliation document')
    }
  }, [])

  const [downloadDocument] = useDownloadReconciliationDocumentMutation({
    onError(error) {
      stopPolling()
      setPending(null)
      Alert.error(error.message)
    },
    async onCompleted(data) {
      const { preSignedUrl } = data?.downloadReconciliationDocument || {}

      if (preSignedUrl) {
        stopPolling()
        setPending(null)
        await saveFile(preSignedUrl)
      }
      // No URL means the job was just queued; the poll started by `download` takes it from here.
    }
  })

  /** Asks the API for the file: returns a URL when it exists, otherwise queues generation. */
  const requestDocument = useCallback(
    (target: PendingDocument) => {
      downloadDocument({
        variables: {
          reconciliationDocumentInput: {
            reconciliationId: target.reconciliationId,
            fileFormat: target.fileFormat,
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        }
      })
    },
    [downloadDocument]
  )

  const [fetchStatus] = useFetchReconciliationDocumentStatusLazyQuery({
    fetchPolicy: 'network-only',
    onError() {
      // A single failed poll is not fatal — the next tick retries until the deadline.
    },
    onCompleted(data) {
      const current = pendingRef.current
      if (!current) return

      const { pdfStatus, csvStatus, documentErrorMessage } = data?.fetchReconciliationDocumentStatus || {}
      const status = current.fileFormat === ReconciliationDocumentFileFormat.Pdf ? pdfStatus : csvStatus

      if (status === ReconciliationDocumentStatus.Completed) {
        stopPolling()
        requestDocument(current)
        return
      }

      if (status === ReconciliationDocumentStatus.Failed) {
        stopPolling()
        setPending(null)
        Alert.error(documentErrorMessage || 'Generating the reconciliation document failed')
      }
    }
  })

  const download = useCallback(
    (reconciliationId: number, fileFormat: ReconciliationDocumentFileFormat) => {
      if (pending) return

      const target = { reconciliationId, fileFormat }
      setPending(target)
      pendingRef.current = target
      deadlineRef.current = Date.now() + POLL_TIMEOUT_MS

      requestDocument(target)

      stopPolling()
      pollRef.current = setInterval(() => {
        if (Date.now() > deadlineRef.current) {
          stopPolling()
          setPending(null)
          Alert.error('The reconciliation document is taking longer than expected. Please try again.')
          return
        }

        fetchStatus({ variables: { id: reconciliationId } })
      }, POLL_INTERVAL_MS)
    },
    [fetchStatus, pending, requestDocument, stopPolling]
  )

  const isDownloading = useCallback(
    (reconciliationId: number, fileFormat?: ReconciliationDocumentFileFormat) =>
      !!pending &&
      pending.reconciliationId === reconciliationId &&
      (!fileFormat || pending.fileFormat === fileFormat),
    [pending]
  )

  return { download, isDownloading, pending }
}
